import { createSlider, getObserver, layoutRecalcManager, layoutPhaseMeasure, layoutPhaseApply } from '@theme/theme-global';

class ImagesComparisonSlider extends HTMLElement {
  constructor() {
    super();
    this.sliderInstances = [];
    this._observer = null;
    this._syncing = false;
    this.recalculateLayout = this.recalculateLayout.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.imageEl = this.querySelector('[data-image-part]');
    this.contentEl = this.querySelector('[data-content-part]');
    this.init();
  }

  disconnectedCallback() {
    layoutRecalcManager.unregister(this.recalculateLayout);
    this._observer?.unobserve(this);
    this._observer = null;
    this.destroySliders();
  }

  destroySliders() {
    this.sliderInstances.forEach((slider) => {
      if (slider && !slider.destroyed) slider.destroy(true, true);
    });
    this.sliderInstances = [];
    this.imageSlider = null;
    this.contentSlider = null;
  }

  init() {
    if (!this.imageEl?.dataset.params || !this.contentEl?.dataset.params) return;

    const sectionIndex = parseInt(this.section?.querySelector('[data-index]')?.dataset?.index ?? '1', 10);
    const aboveTheFoldSection = this.section && sectionIndex <= 2;

    const initSliders = async () => {
      if (this._sliderInitializing) return;
      this._sliderInitializing = true;

      try {
        this.destroySliders();

        const imageParams = JSON.parse(this.imageEl.dataset.params);
        const contentParams = JSON.parse(this.contentEl.dataset.params);
        const paginationEl = this.contentEl.querySelector('[data-pagination]');

        if (paginationEl) {
          contentParams.pagination = {
            el: paginationEl,
            clickable: true,
          };
        }

        const [imageSlider, contentSlider] = await Promise.all([
          createSlider(this.imageEl, imageParams),
          createSlider(this.contentEl, contentParams),
        ]);

        if (!imageSlider || !contentSlider) return;

        this.imageSlider = imageSlider;
        this.contentSlider = contentSlider;
        this.sliderInstances = [imageSlider, contentSlider];
        this.syncSliders();
        layoutRecalcManager.register(this.recalculateLayout, { root: this });
      } finally {
        this._sliderInitializing = false;
      }
    };

    if (aboveTheFoldSection) {
      initSliders();
    } else if ('IntersectionObserver' in window) {
      this._observer = getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false);
      this._observer.observe(this, () => {
        initSliders();
      });
    } else {
      initSliders();
    }
  }

  syncSliders() {
    if (!this.imageSlider || !this.contentSlider) return;

    this.contentSlider.on('slideChange', () => {
      if (this._syncing) return;
      this._syncing = true;
      const index = this.contentSlider.realIndex;
      if (this.imageSlider.realIndex !== index) {
        this.imageSlider.slideTo(index);
      }
      this._syncing = false;
    });
  }

  recalculateLayout(phase) {
    if (layoutPhaseMeasure(phase)) {
      this._pendingWidth = window.innerWidth;
    }

    if (layoutPhaseApply(phase)) {
      const width = this._pendingWidth ?? window.innerWidth;
      if (width === this._lastWidth) return;
      this._lastWidth = width;

      this.sliderInstances.forEach((slider) => {
        if (slider && !slider.destroyed) slider.update();
      });
    }
  }
}

if (!customElements.get('images-comparison-slider')) {
  customElements.define('images-comparison-slider', ImagesComparisonSlider);
}

class ImageComparison extends HTMLElement {
  constructor() {
    super();
    this.startDrag = this.onStartDrag.bind(this);
    this.drag = this.onDrag.bind(this);
    this.stopDrag = this.onStopDrag.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.image-comparison-button');
    if (!this.button) return;

    this.isHorizontal = this.dataset.direction !== 'vertical';
    this.buttonIcon = this.button.querySelector('span');
    this.init();
    this.initAnimation();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('touchstart', this.startDrag);
    this.button?.removeEventListener('mousedown', this.startDrag);
    this.button?.removeEventListener('keydown', this.onKeyDown);
    this.stopDrag();
    this._animationObserver?.disconnect();
    this._animationObserver = null;
  }

  init() {
    this.button.setAttribute('role', 'slider');
    this.button.setAttribute('aria-orientation', this.isHorizontal ? 'horizontal' : 'vertical');
    this.button.setAttribute('aria-valuemin', '0');
    this.button.setAttribute('aria-valuemax', '100');
    this.syncAriaValue(this.getPercent());

    this.button.addEventListener('touchstart', this.startDrag, { passive: false });
    this.button.addEventListener('mousedown', this.startDrag);
    this.button.addEventListener('keydown', this.onKeyDown);
  }

  initAnimation() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('is-visible', '');
      return;
    }

    this._animationObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.setAttribute('is-visible', '');
        this.classList.add('is-animating');
        setTimeout(() => this.classList.remove('is-animating'), 1000);
        this._animationObserver?.disconnect();
        this._animationObserver = null;
      },
      { threshold: 0.25 },
    );
    this._animationObserver.observe(this);
  }

  getPercent() {
    const raw = getComputedStyle(this).getPropertyValue('--percent').trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 50;
  }

  getOffset() {
    return this.buttonIcon ? this.buttonIcon.getBoundingClientRect().width / 2 : 20;
  }

  setPercent(percent, rect = this.getBoundingClientRect()) {
    const distance = this.isHorizontal ? rect.width : rect.height;
    if (!distance) return;

    const offset = this.offset ?? this.getOffset();
    const min = (offset * 100) / distance;
    const max = ((distance - offset) * 100) / distance;
    const clamped = Math.max(min, Math.min(percent, max));

    this.style.setProperty('--percent', `${clamped}%`);
    this.syncAriaValue(clamped);
  }

  syncAriaValue(percent) {
    this.button?.setAttribute('aria-valuenow', String(Math.round(percent)));
  }

  onKeyDown(e) {
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    const step = e.shiftKey ? 10 : 2;
    let percent = this.getPercent();
    let handled = true;

    switch (e.key) {
      case 'ArrowLeft':
        if (!this.isHorizontal) return;
        percent += isRtl ? step : -step;
        break;
      case 'ArrowRight':
        if (!this.isHorizontal) return;
        percent += isRtl ? -step : step;
        break;
      case 'ArrowUp':
        if (this.isHorizontal) return;
        percent -= step;
        break;
      case 'ArrowDown':
        if (this.isHorizontal) return;
        percent += step;
        break;
      case 'Home':
        percent = 0;
        break;
      case 'End':
        percent = 100;
        break;
      default:
        handled = false;
    }

    if (!handled) return;
    e.preventDefault();
    this.setPercent(percent);
  }

  onStartDrag(e) {
    e.preventDefault();
    this.classList.add('is-dragging');
    this._rect = this.getBoundingClientRect();
    this.offset = this.getOffset();

    if (e.type === 'mousedown') {
      document.addEventListener('mousemove', this.drag);
      document.addEventListener('mouseup', this.stopDrag);
    } else if (e.type === 'touchstart') {
      document.addEventListener('touchmove', this.drag, { passive: false });
      document.addEventListener('touchend', this.stopDrag);
    }
  }

  onStopDrag() {
    this.classList.remove('is-dragging');
    document.removeEventListener('mousemove', this.drag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.drag);
    document.removeEventListener('touchend', this.stopDrag);
  }

  onDrag(e) {
    if (e.cancelable) e.preventDefault();
    const event = (e.touches && e.touches[0]) || e;
    const rect = this._rect;
    if (!rect) return;

    let position;
    let distance;

    if (this.isHorizontal) {
      position = event.clientX - rect.left;
      distance = rect.width;
      if (document.documentElement.getAttribute('dir') === 'rtl') {
        position = distance - position;
      }
    } else {
      position = event.clientY - rect.top;
      distance = rect.height;
    }

    this.setPercent((position * 100) / distance, rect);
  }
}

if (!customElements.get('image-comparison')) {
  customElements.define('image-comparison', ImageComparison);
}
